// The PURE half of the lobby's world picker (M6, task 18b): response-payload
// validation, the create-form name rules, and every Romanian string the picker
// renders. No DOM, no fetch, no three.js — `worlds-view.test.mjs` runs it
// headless, and asserts the name rules agree with the SERVER's `parseWorldName`
// by importing both real implementations (never by re-typing one of them).
//
// The DOM + fetch half is `mountWorldPicker` in `lobby.ts`.

import { displayName } from '../ui/menu-model.js'

/**
 * Same limit as the server (`server/http/routes.ts` MAX_WORLD_NAME). Duplicated
 * rather than imported — a client module must never reach into `src/server/` —
 * and pinned equal by the test, so the two cannot drift.
 */
export const MAX_WORLD_NAME = 40

/**
 * How often the lobby re-asks `/api/worlds` while it is on screen. Polling
 * stops the moment a world is picked — the game does not keep a lobby timer.
 */
export const WORLDS_POLL_MS = 5000

/** Mirror of the server's `WorldSummary` (see `server/http/routes.ts`). */
export interface WorldSummary {
  id: number
  name: string
  /** Creator's email; the lobby shows `displayName(createdBy)`. */
  createdBy: string
  createdAt: number
  lastPlayedAt: number | null
  /** LIVE player count from the room registry — "acum", not history. */
  players: number
  /**
   * Soft-deleted (admin lobby management). The server only ever sends true to
   * an ADMIN — everyone else's list simply omits hidden worlds — so the picker
   * renders it as a restorable "ascunsă" row, never an error.
   */
  hidden: boolean
}

export type WorldNameValidation =
  | { ok: true; value: string }
  | { ok: false; message: string }

/**
 * Validate the "Lume nouă" form. Same rules as the server: trimmed, non-empty,
 * at most MAX_WORLD_NAME chars AFTER trimming. Client-side is a courtesy for a
 * friendly Romanian message; the server re-checks and answers `invalid_name`.
 */
export function validateWorldName(input: string): WorldNameValidation {
  const trimmed = input.trim()
  if (trimmed.length === 0) {
    return { ok: false, message: 'Dă-i un nume lumii tale. ✏️' }
  }
  if (trimmed.length > MAX_WORLD_NAME) {
    return { ok: false, message: `Numele e prea lung — cel mult ${MAX_WORLD_NAME} de caractere.` }
  }
  return { ok: true, value: trimmed }
}

/**
 * "nimeni" / "1 jucător" / "3 jucători" / "21 de jucători" — Romanian counts
 * numbers of 20 and above with "de".
 */
export function playersLabel(players: number): string {
  if (players === 0) return 'nimeni'
  if (players === 1) return '1 jucător'
  if (players >= 20) return `${players} de jucători`
  return `${players} jucători`
}

/** The row's second line: "de ana • 2 jucători acum" (+ the hidden badge). */
export function worldMeta(world: WorldSummary): string {
  const base = `de ${displayName(world.createdBy)} • ${playersLabel(world.players)} acum`
  return world.hidden ? `${base} • ascunsă 🙈` : base
}

// Romanian copy for the admin-only per-row controls (lobby.ts renders them;
// keeping the strings here keeps them headless-testable and in one place).
export const RENAME_LABEL = '✏️ Redenumește'
export const RENAME_SAVE_LABEL = 'Salvează ✅'
export const HIDE_LABEL = '🙈 Ascunde'
export const RESTORE_LABEL = '🌞 Arată din nou'
export const RENAME_PLACEHOLDER = 'Noul nume al lumii'
export const RENAME_DONE_MESSAGE = 'Gata, am redenumit-o! ✅'
export const HIDDEN_DONE_MESSAGE = 'Am ascuns-o. O poți arăta oricând. 🙈'
export const RESTORED_DONE_MESSAGE = 'Lumea e înapoi în listă! 🌞'

// The admin-only backup download. The endpoint is per-DATABASE (it streams the
// newest rotating copy of the whole sqlite file — every world at once), so this
// is ONE card in the admin area, never a per-row control.
export const BACKUP_URL = '/api/admin/backup'
export const BACKUP_LABEL = '🗄️ Descarcă lumea'
export const BACKUP_TITLE = 'Copie de siguranță 🗄️'
export const BACKUP_HINT =
  'Descarcă cea mai nouă copie a bazei de date (toate lumile). Ține-o undeva în siguranță.'
export const BACKUP_FILENAME = 'voxelcraft-backup.db'

// The listing (backup-list): the human sees WHAT copies exist before pulling one.
export const BACKUPS_URL = '/api/admin/backups'
export const BACKUPS_EMPTY = 'Nicio copie încă — prima se face la pornirea serverului. ⏳'
export const BACKUPS_ERROR = 'Nu pot citi lista copiilor. 🤔'
export const BACKUP_DOWNLOAD_ONE = '⬇️'

/** One retained copy, as `GET /api/admin/backups` sends it. */
export interface BackupEntry {
  /** Filename only — the handle the server membership-tests before serving. */
  name: string
  size: number
  /** UTC ms the copy was taken. */
  takenAt: number
}

export function parseBackupEntry(value: unknown): BackupEntry | null {
  if (typeof value !== 'object' || value === null) return null
  const e = value as Record<string, unknown>
  if (typeof e.name !== 'string' || e.name.length === 0) return null
  if (typeof e.size !== 'number' || !Number.isFinite(e.size) || e.size < 0) return null
  if (typeof e.takenAt !== 'number' || !Number.isFinite(e.takenAt)) return null
  return { name: e.name, size: e.size, takenAt: e.takenAt }
}

/** `{ backups: [...] }` → entries, malformed ones dropped. null = not that shape. */
export function parseBackupsPayload(body: unknown): BackupEntry[] | null {
  if (typeof body !== 'object' || body === null) return null
  const { backups } = body as Record<string, unknown>
  if (!Array.isArray(backups)) return null
  const out: BackupEntry[] = []
  for (const entry of backups) {
    const parsed = parseBackupEntry(entry)
    if (parsed) out.push(parsed)
  }
  return out
}

/** "3,4 MB" / "812 kB" / "97 B" — Romanian decimal comma, one decimal from kB up. */
export function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 0) return '—'
  if (size < 1000) return `${Math.round(size)} B`
  const units = ['kB', 'MB', 'GB', 'TB']
  let value = size / 1000
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000
    unit++
  }
  return `${value.toFixed(1).replace('.', ',')} ${units[unit]}`
}

/** "11.07.2026, 18:12 UTC" — the stamp the FILE carries, so always UTC. */
export function formatBackupStamp(takenAt: number): string {
  const d = new Date(takenAt)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number): string => String(n).padStart(2, '0')
  return (
    `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`
  )
}

/** One row's label: "11.07.2026, 18:12 UTC • 3,4 MB". */
export function backupRowLabel(entry: BackupEntry): string {
  return `${formatBackupStamp(entry.takenAt)} • ${formatBytes(entry.size)}`
}

/**
 * The href that downloads ONE listed copy. The name is a server-issued listing
 * entry and is URL-ENCODED here; the server still membership-tests it against
 * planRotation().keep — the client never gets to pick a path.
 */
export function backupHref(name: string): string {
  return `${BACKUP_URL}?name=${encodeURIComponent(name)}`
}

/** One world object from the wire, or null if it doesn't hold the shape. */
export function parseWorldSummary(value: unknown): WorldSummary | null {
  if (typeof value !== 'object' || value === null) return null
  const w = value as Record<string, unknown>
  if (!Number.isInteger(w.id) || (w.id as number) <= 0) return null
  if (typeof w.name !== 'string' || typeof w.createdBy !== 'string') return null
  if (typeof w.createdAt !== 'number' || !Number.isFinite(w.createdAt)) return null
  if (w.lastPlayedAt !== null && typeof w.lastPlayedAt !== 'number') return null
  if (!Number.isInteger(w.players) || (w.players as number) < 0) return null
  // `hidden` arrived with the admin soft-delete; absent (an older server mid-
  // deploy) reads as visible, any non-boolean junk is refused.
  if (w.hidden !== undefined && typeof w.hidden !== 'boolean') return null
  return {
    id: w.id as number,
    name: w.name,
    createdBy: w.createdBy,
    createdAt: w.createdAt,
    lastPlayedAt: w.lastPlayedAt as number | null,
    players: w.players as number,
    hidden: w.hidden === true
  }
}

/**
 * The GET /api/worlds payload: an array of summaries, malformed entries
 * dropped. `null` when the body isn't an array at all (an error body, a proxy
 * hiccup) so the caller keeps the last good list instead of blanking the UI.
 */
export function parseWorldsPayload(body: unknown): WorldSummary[] | null {
  if (!Array.isArray(body)) return null
  const worlds: WorldSummary[] = []
  for (const entry of body) {
    const world = parseWorldSummary(entry)
    if (world) worlds.push(world)
  }
  return worlds
}

/** Server error codes for the world routes -> Romanian. */
const WORLD_ERROR_MESSAGES: Record<string, string> = {
  invalid_name: 'Numele nu e bun — încearcă unul între 1 și 40 de litere.',
  not_authenticated: 'Nu ești conectat. Intră în cont mai întâi.',
  world_not_found: 'Nu mai găsesc lumea asta — reîmprospătez lista. 🔍',
  admin_only: 'Doar administratorul poate face asta. 👑',
  network: 'Serverul nu răspunde. Verifică conexiunea. ❌'
}

const FALLBACK_MESSAGE = 'Ceva n-a mers. Mai încearcă o dată. 🤔'

/** Map a world-route error code to Romanian; never show a raw code to a kid. */
export function describeWorldError(code: unknown): string {
  if (typeof code !== 'string') return FALLBACK_MESSAGE
  return WORLD_ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE
}
